import { keepPreviousData } from '@tanstack/react-query';
import { Descriptions, Flex } from 'antd';
import {
  TotalEntries,
  useGetClassifierMappingQuery,
  CategoryMappingDetailTable,
  Padding,
  ObjectHeader,
  ActionButton,
  DetailLayout,
  PopconfirmDelete,
  useClassifierMappingMutation,
  useMutationErrors,
  GetApiErrorMessage,
  ErrorAlertWithDrawer,
  useShowLockData,
  ObjectType,
  LockedBy,
  useCheckLockAndNavigate,
  isLocked,
  DropdownItem,
  formatCodeName,
  ClassifierMappingsFormDataType,
  Spinner,
  useHasPermission,
  PermissionCode,
  useAlertContext,
  InfoIconUrl,
} from '@atlas-frontend/sdk';
import { FC, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { path } from '../../paths';
import { useNavigateTo } from '../../paths/useNavigateTo';
import { ClassifierMappingBreadcrumbs } from './ClassifierMappingBreadcrumbs';
import {
  ContentWrapper,
  DescriptionsWrapper,
} from './ClassifierMappingsDetail.styles';
import { ClassifierMappingExcelActions } from './excel/ClassifierMappingExcelActions';
import translations from './ClassifierMappingDetail.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';

const { addTranslations, useTranslation, t } = i18nNsFactory(
  'category-mapping-detail',
);
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t(`alert.error.code.${code}`, meta?.args && { ...meta.args });

export const ClassifierMappingDetail = () => {
  const params = useParams();
  const classifierMappingId = Number(params.id);
  if (isNaN(classifierMappingId))
    throw new Error('Classifier mapping ID not provided');

  const { data: classifierMapping, isLoading } = useGetClassifierMappingQuery(
    classifierMappingId,
    { placeholderData: keepPreviousData },
  );

  if (isLoading) {
    return <Spinner />;
  }

  if (!classifierMapping) {
    return null;
  }

  return (
    <ClassifierMappingDetailInner
      classifierMappingId={classifierMappingId}
      classifierMapping={classifierMapping}
    />
  );
};

interface ClassifierMappingDetailInnerProps {
  classifierMappingId: number;
  classifierMapping: ClassifierMappingsFormDataType;
}

export const ClassifierMappingDetailInner: FC<
  ClassifierMappingDetailInnerProps
> = (props) => {
  const { classifierMappingId, classifierMapping } = props;
  const { t } = useTranslation();
  const navigateToClassifierMappingEdit = useNavigateTo(
    path.classifierMappingEdit,
    { id: classifierMappingId },
  );
  const navigateToClassifierMappingClone = useNavigateTo(
    path.classifierMappingClone,
    { id: classifierMappingId },
  );
  const navigateToIndex = useNavigateTo(path.classifierMappingDirectory);

  const categoryMappingCount = useMemo(
    () => classifierMapping.categoryMappings.length || 0,
    [classifierMapping.categoryMappings.length],
  );

  const { mutate } = useClassifierMappingMutation();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);

  const { lockData, refetchLockData } = useShowLockData(
    ObjectType.ClassifierMapping,
    classifierMappingId,
  );

  const handleCancel = useCallback(() => navigateToIndex(), [navigateToIndex]);

  const checkLockAndNavigateToEdit = useCheckLockAndNavigate(
    refetchLockData,
    lockData,
    navigateToClassifierMappingEdit,
  );
  const { setSuccess } = useAlertContext();

  const handleDelete = useCallback(() => {
    clearMutationErrors();
    mutate(
      {
        id: classifierMappingId,
      },
      {
        onSuccess: () => {
          navigateToIndex();
          setSuccess(t('alert.success.delete'));
        },
        onError: (apiErrorResponse) => {
          setMutationErrors(apiErrorResponse.response?.data.errors || []);
        },
      },
    );
  }, [
    clearMutationErrors,
    mutate,
    classifierMappingId,
    navigateToIndex,
    setSuccess,
    t,
    setMutationErrors,
  ]);

  const hasPermissionToManage = useHasPermission(
    PermissionCode.CLASSIFIER_MANAGE,
  );

  const dropdownItems = useMemo(
    () =>
      hasPermissionToManage
        ? [
            {
              key: 'clone',
              label: (
                <DropdownItem
                  onClick={() => navigateToClassifierMappingClone()}
                >
                  {t('button.clone.label')}
                </DropdownItem>
              ),
            },
            {
              disabled: isLocked(lockData),
              key: 'delete',
              label: (
                <PopconfirmDelete
                  stopClickPropagation
                  onDelete={handleDelete}
                  genitiveModelName={t('delete.genitive')}
                />
              ),
            },
          ]
        : [],
    [
      navigateToClassifierMappingClone,
      handleDelete,
      lockData,
      t,
      hasPermissionToManage,
    ],
  );

  const descriptionItems = useMemo(
    () => [
      {
        key: 'code',
        label: t('field.code.label'),
        children: classifierMapping.code,
      },
      {
        key: 'name',
        label: t('field.name.label'),
        children: classifierMapping.name,
      },
      {
        key: 'classifierFrom',
        label: t('field.classifierFrom.label'),
        children: formatCodeName(classifierMapping.classifierFrom),
      },
      {
        key: 'classifierTo',
        label: t('field.classifierTo.label'),
        children: formatCodeName(classifierMapping.classifierTo),
      },
      {
        key: 'description',
        label: t('field.description.label'),
        children: classifierMapping.description,
      },
    ],
    [classifierMapping, t],
  );

  return (
    <DetailLayout
      infoIconUrl={InfoIconUrl.CLASSIFIER_MAPPING_DETAIL}
      type="detail"
      side={{
        header: (
          <Padding>
            <ObjectHeader
              upperText={classifierMapping.code}
              lowerText={classifierMapping.name}
            />
          </Padding>
        ),
        content: (
          <Padding>
            <DescriptionsWrapper>
              <Descriptions items={descriptionItems} column={1} />
            </DescriptionsWrapper>
          </Padding>
        ),
        footer: (
          <LockedBy
            id={classifierMappingId}
            entity={{
              ...classifierMapping,
              id:
                classifierMapping.id === null
                  ? undefined
                  : classifierMapping.id,
            }}
            objectType={ObjectType.ClassifierMapping}
          />
        ),
      }}
      breadCrumb={<ClassifierMappingBreadcrumbs data={{ classifierMapping }} />}
      footerContent={<TotalEntries count={categoryMappingCount} />}
      headerConfig={{
        actions: (
          <>
            <ClassifierMappingExcelActions id={classifierMappingId} />
            <ActionButton
              type="abort"
              layoutType="detail"
              onClick={handleCancel}
            />
            {hasPermissionToManage && (
              <ActionButton
                disabled={isLocked(lockData)}
                type="mainAction"
                layoutType="detail"
                onClick={() => checkLockAndNavigateToEdit()}
              />
            )}
          </>
        ),
        dropdownMenu: hasPermissionToManage
          ? {
              items: dropdownItems,
            }
          : undefined,
      }}
      alert={<ErrorAlertWithDrawer validationMessagesMap={mutationErrors} />}
    >
      <Padding>
        <ContentWrapper>
          <Flex vertical gap="large" align="center">
            <CategoryMappingDetailTable
              categoryMappings={classifierMapping.categoryMappings}
            />
          </Flex>
        </ContentWrapper>
      </Padding>
    </DetailLayout>
  );
};
